import React, { createContext, useContext, useState, useMemo, useEffect, useCallback, ReactNode, FC } from "react";
import { PermissionContextType, Resource } from "./permissionType";
import { API_ENDPOINTS } from "../global-provider/api-shell/api-provider";
import apiGetFromAuth from "../../../api-controller-mod/api-serv/get-api/getApiFromAuth";
import { normalizePermissions } from "./normalisePermission";
import { useCookies } from "../global-provider/cookies-context/cookiesContext";

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [permissionMap, setPermissionMap] = useState<Map<string, boolean>>(new Map());
  const [loading, setLoading] = useState(true);
  const { authReady } = useCookies();

  const fetchPermissions = useCallback(async () => {
    setLoading(true);

    try {
      const response = await apiGetFromAuth(API_ENDPOINTS.GET_ALL_PERMISSION_CMS);
      const data: Resource[] = response.data?.data || [];

      setResources(data);
      setPermissionMap(normalizePermissions(data));
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    if (!authReady) return;
    fetchPermissions();
  }, [authReady, fetchPermissions]);

  //Check if a user has a specific action
  const hasAction = useCallback(
    (resource: string, permission: string, action: string): boolean => {
      return permissionMap.get(`${resource}.${permission}.${action}`) === true;
    },
    [permissionMap]
  );

  const value = useMemo(
    () => ({
      resources,
      setResources,
      hasAction,
      reloadPermissions: fetchPermissions,
      permissionMap,
      loading
    }),
    [resources, permissionMap, hasAction, fetchPermissions, loading]
  );

  return <PermissionContext.Provider value={value}>
    {children}
  </PermissionContext.Provider>;
};

// Hook to access permissions in components
export const usePermission = () => {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error("usePermi ssion must be used within PermissionProvider");
  return ctx;
};
